'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Calendar, Clock, Video, Trash2, Play, Trash } from 'lucide-react';
import { useMeetingHistory } from '@/hooks/useMeetingHistory';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';

export default function MeetingHistory() {
  const { meetings, deleteMeeting, markAsAccessed, clearAllMeetings } = useMeetingHistory();
  const router = useRouter();
  const [isClearing, setIsClearing] = useState(false);

  const handleDeleteMeeting = (id: string) => {
    deleteMeeting(id);
  };

  const handleClearAllMeetings = async () => {
    setIsClearing(true);
    try {
      clearAllMeetings();
    } finally {
      setIsClearing(false);
    }
  };

  const handleJoinMeeting = (meeting: any) => {
    // Criar nome de sala limpo para URL
    const cleanRoomName = meeting.name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
      .replace(/\s+/g, '-') // Substitui espaços por hífens
      .replace(/-+/g, '-') // Remove hífens duplos
      .replace(/^-|-$/g, ''); // Remove hífens no início/fim

    // Armazenar dados no sessionStorage
    sessionStorage.setItem('originalRoomName', meeting.name);
    sessionStorage.setItem('currentMeetingId', meeting.id);

    // Marcar como acessada
    markAsAccessed(meeting.id);

    // Navegar para a sala
    router.push(`/${cleanRoomName}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  return (
    <Card className="w-full bg-white border-gray-200 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-900">
            <Calendar className="w-5 h-5" />
            <CardTitle>Histórico de Reuniões</CardTitle>
          </div>
          {meetings.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  className="text-sm text-gray-400 hover:text-red-600 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isClearing}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Limpar histórico</span>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-gray-900 font-semibold">
                    Limpar Histórico de Reuniões
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-2">
                      <span className="text-gray-600 block">
                        Tem certeza que deseja limpar todo o histórico de reuniões? Esta ação não
                        pode ser desfeita.
                      </span>
                      <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-600 border border-red-100">
                        <Trash2 className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">
                          {meetings.length} reunião{meetings.length !== 1 ? 'ões' : ''} serão
                          removida{meetings.length !== 1 ? 's' : ''} permanentemente.
                        </span>
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="border-t border-gray-100">
                  <AlertDialogCancel className="bg-white border-gray-200 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 text-gray-600">
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleClearAllMeetings}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {isClearing ? (
                      <>
                        <span className="animate-pulse">Limpando</span>...
                      </>
                    ) : (
                      'Sim, Limpar Tudo'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {meetings.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nenhuma reunião encontrada</p>
            <p className="text-sm">Suas reuniões aparecerão aqui após serem criadas</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {meetings.map((meeting) => (
                <div
                  key={meeting.id}
                  className={`rounded-lg p-4 border transition-colors relative ${
                    meeting.status === 'ongoing'
                      ? 'bg-gray-50 border-gray-200 hover:bg-blue-50 hover:border-blue-200 cursor-pointer'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                  onClick={() => meeting.status === 'ongoing' && handleJoinMeeting(meeting)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-gray-900">{meeting.name}</h3>
                        {meeting.status === 'ongoing' && (
                          <Video data-testid="video-icon" className="w-4 h-4 text-blue-600" />
                        )}
                      </div>

                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(meeting.date)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Play className="w-4 h-4" />
                          {formatTime(meeting.createdAt)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {meeting.duration}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-gray-100 border-gray-200 text-gray-500 hover:text-red-600 hover:bg-red-50 hover:border-red-200 shadow-sm"
                        aria-label="trash"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMeeting(meeting.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
